import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Observable, Subject } from 'rxjs';

import { ApiService } from '../api/api.service';
import { LocalStorageService } from './local-storage.service';

import { IAuthDTO } from '../api/models/v1/auth';
import { IRetailerDTO } from '../api/models/v1/retailer';
import { IRetailerSubscriptionDTO } from '../api/models/v1/retailer-subscription';
import { IUserDTO } from '../api/models/v1/user';
import { SubscriptionTierEnum } from '../enums/subscription-tier';
import { UserPermissionEnum } from '../enums/user-permission';
import { UserRoleEnum } from '../enums/user-role';



dayjs.extend(utc);

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public user$: Observable<IUserDTO | null>;
    private userSubject: Subject<IUserDTO | null>;
    private _user: IUserDTO | null = null;

    private timeout: any;

    constructor(private api: ApiService,
        private localStorageService: LocalStorageService,
        private router: Router) {
        this.userSubject = new Subject<IUserDTO | null>();
        this.user$ = this.userSubject.asObservable();
    }

    public getUser(): IUserDTO | null {
        return this._user;
    }

    set user(newUser: IUserDTO | null) {
        this._user = newUser;
        this.userSubject.next(newUser);
    }

    public async getRetailer(): Promise<IRetailerDTO>{
        return await this.api.retailers.getByAuthAsync();
    }
    
    public async getActiveSubscription(): Promise<IRetailerSubscriptionDTO>{
        return await this.api.retailers.getActiveSubscription();
    }

    public getHighestRole(): string {
        const user = this.getUser();
        return !user ? '' : user.roles.findIndex(x => x.role == UserRoleEnum.Website && x.permission == UserPermissionEnum.Admin) > -1 ? 'Admin' : 'User';
    }

    public async refreshAuthUserAsync(): Promise<boolean> {
        try {
            const result = await this.refreshTokenAsync();
            this.user = await this.api.users.getByAuthAsync();

            return result;
        } catch (error) {
        }

        return false;
    }

    public async setAuth(auth: IAuthDTO): Promise<boolean> {
        try {
            this.localStorageService.auth.set(auth);

            this.startRefreshAsync();
            this.user = await this.api.users.getByAuthAsync();
            return true;
        } catch {
            return false;
        }
    }

    public async loginAsync(email: string, password: string): Promise<boolean> {
        try {
            const auth = await this.api.auth.loginAsync({
                email,
                password
            });

            return this.setAuth(auth);
        } catch {
            return false;
        }
    }

    /**
     * Checks to see if the renew timer has been started
     */
    public hasStartedTimer(): boolean {
        return this.timeout ? true : false;
    }

    /**
     * Checks to see if the user is authenticated
     */
    public isAuthenticated(): boolean {
        const authValue = this.localStorageService.auth.get();

        if (authValue && authValue.token && authValue.expiresDateTime && authValue.expiresDateTime > dayjs.utc().toDate()) {
            return true;
        }

        return false;
    }

    /**
     * Checks to see if the authenticated user has the given role (roles found in AuthRoles)
     */
    public hasRole(role: string): boolean {
        const roles = this.getRoles();
        return roles.findIndex(x => x == role) > -1;
    }

    /**
     * Gets the active subscription tier id from the roles in the auth token
     * @returns active subscription tier id
     */
    public getActiveSubscriptionId(): SubscriptionTierEnum | null {
        const roles = this.getRoles();
        const subscription = roles.find(x => x.startsWith('Subscription_'));
        if (!subscription) return null;

        switch(+subscription.replace('Subscription_', '')){
            case 1: return SubscriptionTierEnum.Basic;
            case 2: return SubscriptionTierEnum.Business;
            case 3: return SubscriptionTierEnum.Premium;
            case 4: return SubscriptionTierEnum.Custom;
            case 5: return SubscriptionTierEnum.Free;
            case 6: return SubscriptionTierEnum.Starter;
            case 7: return SubscriptionTierEnum.Custom_Slider;
            default: return null;
        }
    }

    /**
     * Starts the renew timer
     */
    public async startRefreshAsync() {
        if (!this._user) {
            this.user = await this.api.users.getByAuthAsync();
        }

        if (!this.timeout) {
            this.refreshAsync();
        }
    }

    /**
     * Logs the current user out (clears local storage, clears renew timer)
     */
    public logout(): void {
        this.localStorageService.auth.clear();

        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        this.user = null;
    }

    private getRoles(): string[] {
        const authValue = this.localStorageService.auth.get();

        if (authValue && authValue.token && authValue.expiresDateTime && authValue.expiresDateTime > dayjs.utc().toDate()) {

            const token = JSON.parse(window.atob(authValue.token.split('.')[1]));

            if (token) {
                const result = <string[]>token.role;

                if (typeof result == 'string') return [result];

                return result;
            }
        }

        return [];
    }

    /**
     * Renews the auth token once an hour
     */
    private async refreshAsync(): Promise<void> {
        if (this.isAuthenticated()) {
            const authValue = this.localStorageService.auth.get();

            const expiresInSeconds = !authValue?.expiresDateTime ? 0 : dayjs.utc(authValue?.expiresDateTime).diff(dayjs.utc(), 'second');
            const oneHour = ((1000 * 60) * 60);

            if ((expiresInSeconds * 1000) <= oneHour) {  // If less then 1 hour left refresh now
                if (!await this.refreshTokenAsync()) {
                    this.logout();
                    this.router.navigate(['/', 'login']);
                } else {
                    this.refreshAsync();
                }
            } else {
                if (this.timeout) {
                    clearTimeout(this.timeout);
                    this.timeout = null;
                }
                this.timeout = setTimeout(async () => {
                    if (!await this.refreshTokenAsync()) {
                        this.logout();
                        this.router.navigate(['/', 'login']);
                    } else {
                        this.refreshAsync();
                    }
                }, oneHour);
            }

        }
    }

    /**
     * Refreshes the token
     */
    public async refreshTokenAsync(): Promise<boolean> {
        try {
            const auth = await this.api.auth.refreshAsync();

            if (!auth) {
                return false;
            }

            this.localStorageService.auth.set(auth);

            return true;

        } catch {
            return false;
        }
    }

    /**
     * Rehydrate auth state from LocalStorage on app startup.
     * Uses the existing LocalStorageService wrapper and ApiService.
     */
    public async initFromStorage(): Promise<void> {
        try {
            const auth = this.localStorageService.auth.get();
            // If there is no auth in storage, or it is expired, clear and bail.
            if (!auth || !auth.token || !auth.expiresDateTime) {
                return;
            }

            if (auth.expiresDateTime <= dayjs.utc().toDate()) {
                this.localStorageService.auth.clear();
                return;
            }

            // Make sure the refresh timer is running and user is loaded.
            // startRefreshAsync() already fetches the user if missing.
            await this.startRefreshAsync();

            // If for any reason user is still null, fetch explicitly.
            if (!this._user) {
                this.user = await this.api.users.getByAuthAsync();
            }
        } catch {
            // Swallow errors; app can still boot and show login.
        }
    }
}
